import { Formik } from "formik";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Input } from "reactstrap";
import * as Yup from "yup";
import UserContext from "../../../context/userContext";
import Configure2FA from "../../../modules/authentication/Configure2FA";
import { changePassword } from "../../../services/profileService";
import { getCurrentTenantInformation } from "../../../services/sessionService";
import useJwt from "../../../services/useJwt";
import { TenantSessionData } from "../../../types";
import { GetTenantInformationOutput } from "../../../types/apiTypes";
import { TwoFactorTypes } from "../../../utility/constants";
import Message from "../../../utility/notifications/Message";
import Notify from "../../../utility/notifications/Notify";
import { isHostUser } from "../../../utility/Utils";
import AppFormLabel from "../forms/AppFormLabel";
import AppTextInput from "../forms/AppTextInput";
import AppButton from "./AppButton";
import OutOfOfficeForm from "./OutOfOfficeForm";
import PageTitle from "./PageTitle";

const validationSchemaPassword = Yup.object().shape({
  currentPassword: Yup.string().required().label("Current password"),
  newPassword: Yup.string().required().label("Password"),
  newPasswordConfirmation: Yup.string()
    .required()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .label("Repeat password"),
});

const Profile = () => {
  const [isSavingUser, setIsSavingUser] = useState(false);
  const { currentUser } = useContext(UserContext);
  const interfaceVersion = process.env.REACT_APP_INTERFACE_VERSION;
  const [sessionData, setSessionData] = useState<
    GetTenantInformationOutput | undefined
  >();

  const handleTwoFactorSetupComplete = async () => {
    await getTenantSessionData();
    Notify.success(`2FA has been configured successfully`);
  };

  async function handleResetTwoFactorAuth() {
    const messageResponse = await Message(
      "Multi factor authenticator reset",
      "Are you sure you want to reset the Multi factor authenticator?",
      "warning"
    );

    if (messageResponse.isConfirmed) {
      await useJwt
        .resetTwoFactorAuthenticator({
          userId: currentUser.userId,
        })
        .then(async (response) => {
          await getTenantSessionData();
        });
    }
  }

  async function getTenantSessionData() {
    const result = await getCurrentTenantInformation();
    const sessionResult = result.data.result as TenantSessionData;

    if (sessionResult) {
      setSessionData(sessionResult);
    }
  }

  useEffect(() => {
    getTenantSessionData();
  }, []);

  const handlePasswordChangeSubmit = async (pwReset, { resetForm }) => {
    setIsSavingUser(true);

    await changePassword(pwReset)
      .then((res) => {
        if (res.status === 200) {
          setIsSavingUser(false);
          Message(
            "Password updated",
            "Your password has been changed",
            "success"
          );

          resetForm({
            currentPassword: "",
            newPassword: "",
            newPasswordConfirmation: "",
          });
        }
      })
      .catch((err) => {
        if (err.response) {
          setIsSavingUser(false);

          Message(
            err.response.data.error.message,
            err.response.data.error.details,
            "error"
          );
        }
      });
  };

  const isTwoFactorConfigured =
    sessionData?.isTwoFactorRequired &&
    sessionData?.twoFactorPreference !== null;

  return (
    <>
      <Row className={"fst-italic text-light-grey"}>
        {interfaceVersion && (
          <div className={"d-flex justify-content-end gap-1"}>
            <span>Interface version:</span>
            <span>{interfaceVersion}</span>
          </div>
        )}
        {sessionData && (
          <div className={"d-flex justify-content-end gap-1"}>
            <span>API version:</span>
            <span>
              {moment(sessionData?.apiReleaseDate).format(" DD-MM-yyyy")}
            </span>
          </div>
        )}
      </Row>
      <PageTitle title="My profile" />

      <Row>
        <Col lg={6} sm={12}>
          <div className="mb-3">
            <AppFormLabel for="floatingInput" label="Email" />

            <Input disabled value={currentUser?.email} />
          </div>
        </Col>
        <Col lg={6} sm={12}>
          <div className="mb-3">
            <AppFormLabel for="floatingInput" label="Name" />

            <Input disabled value={currentUser?.fullName} />
          </div>
        </Col>
      </Row>

      <Row>
        <Col sm={12}>
          <div className="mb-3">
            <AppFormLabel for="floatingInput" label="Roles" />

            <Input disabled value={currentUser?.roles} />
          </div>
        </Col>
      </Row>

      {
          !sessionData?.isOfficeUser && <>
              <hr></hr>

              <PageTitle title="Update password" customStyles="h4"/>

              <Formik
                  enableReinitialize={true}
                  onSubmit={handlePasswordChangeSubmit}
                  validationSchema={validationSchemaPassword}
                  initialValues={{
                      currentPassword: "",
                      newPassword: "",
                      newPasswordConfirmation: "",
                  }}
              >
                  {({handleSubmit}) => (
                      <Form onSubmit={handleSubmit}>
                          <>
                              <Row>
                                  <Col lg={6} sm={12}>
                                      <div className="mb-3">
                                          <AppTextInput
                                              type="password"
                                              name="currentPassword"
                                              label="Your current password"
                                          />
                                      </div>
                                  </Col>
                              </Row>

                              <Row>
                                  <Col lg={6} sm={12}>
                                      <div className="mb-3">
                                          <AppTextInput
                                              type="password"
                                              name="newPassword"
                                              label="Password"
                                          />
                                      </div>
                                  </Col>
                                  <Col lg={6} sm={12}>
                                      <div className="mb-3">
                                          <AppTextInput
                                              type="password"
                                              name="newPasswordConfirmation"
                                              label="Repeat password"
                                          />
                                      </div>
                                  </Col>
                              </Row>
                          </>

                          <Row className="text-end">
                              <Col>
                                  <AppButton
                                      title="Update password"
                                      disabled={isSavingUser}
                                      loading={isSavingUser}
                                      variant="save"
                                      buttonSize="lg"
                                      type="submit"
                                  />
                              </Col>
                          </Row>
                      </Form>
                  )}
              </Formik>
          </>
      }

      <>
            {sessionData?.isTwoFactorRequired && !sessionData?.isOfficeUser && (
                <>
                    <hr/>

                    <PageTitle
                        hideBackButton
                        title="Multi factor authenticator"
                        customStyles="h4"
                    />

                    {isTwoFactorConfigured == false && (
                        <Configure2FA
                            onSuccess={handleTwoFactorSetupComplete}
                            onProfilePage
                        />
                    )}

                    {isTwoFactorConfigured == true && (
                        <>
                            <Row>
                                <Card className="h-100 cursor-pointer border-2">
                                    <Card.Body className="text-center p-4">
                                        <div className="mb-3">
                                            <i className="fas fa-envelope fa-3x text-primary"></i>
                                        </div>
                                        <h5>
                                            {
                                                TwoFactorTypes.find(
                                                    (x) => x.value === sessionData?.twoFactorPreference
                                                )?.label
                                            }
                                        </h5>
                                        <p className="text-muted">
                                            You have configured this as your multi-factor
                                            authentication method.
                                        </p>
                                    </Card.Body>
                                </Card>
                            </Row>
                            <Row className="text-end mt-3">
                                <Col>
                                    <AppButton
                                        onClick={handleResetTwoFactorAuth}
                                        title="Reset MFA"
                                        variant="revert"
                                        buttonSize="lg"
                                    />
                                </Col>
                            </Row>
                        </>
                    )}
                </>
            )}

            {!isHostUser() && (
                <>
                    <h4 className={"my-5 text-center"}>Replacement</h4>
                    <Row>
              <OutOfOfficeForm />
            </Row>
          </>
        )}
      </>
    </>
  );
};

export default Profile;
